import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { API_URL } from '../services/api';
import { toast } from 'sonner';
import { CreditCard, DollarSign, Shield, Lock, CheckCircle, X, Loader2, ArrowLeft } from 'lucide-react';

const PLAN_FEATURES = {
  basic: ['CRM (Sales, Marketing & Support)', 'AI Market Research (5 reports/mo)', 'MPO Engine — 5 of each per month (GTM, emails, social, blogs, pitches)', '1 Team Member', 'Email Support'],
  startup: ['Everything in Basic', 'Marketing Automation', 'Unlimited MPO Engine', 'Surveys & Forms', '1 Web Funnel', '1 LMS Course', '5 Team Members'],
  growth: ['Everything in Startup', 'Unlimited Web Funnels', 'Unlimited LMS Courses', 'Unlimited Team Members', 'Priority Support'],
};

const PaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'growth');
  const [billingCycle, setBillingCycle] = useState(searchParams.get('cycle') || 'monthly');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [fullName, setFullName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponValidating, setCouponValidating] = useState(false);
  const [autopayAvailable, setAutopayAvailable] = useState(true);

  useEffect(() => {
    api.get(`${API_URL}/payments/autopay/available`).then(r => setAutopayAvailable(r.data.available)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`${API_URL}/subscriptions/plans`).then(r => setPlans(r.data)).catch(() => {});
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
    return () => { if (document.body.contains(s)) document.body.removeChild(s); };
  }, []);

  const plan = plans.find(p => p.id === selectedPlan) || {};
  const basePrice = billingCycle === 'annual' ? (plan.annual_price || 0) : (plan.monthly_price || 0);
  const finalAmount = Math.max(1, basePrice - couponDiscount);

  // Keep the URL in sync with the selected plan/cycle so it reflects the choice
  // and survives a refresh (otherwise a reload reverts to the stale query params).
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('plan', selectedPlan);
      p.set('cycle', billingCycle);
      return p;
    }, { replace: true });
  }, [selectedPlan, billingCycle, setSearchParams]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponValidating(true);
    try {
      const r = await api.post(`${API_URL}/payments/validate-coupon`, { coupon_code: couponCode, email: email.toLowerCase() });
      if (r.data.valid) {
        setCouponApplied(true);
        setCouponDiscount(r.data.discount_amount);
        toast.success(r.data.message);
      } else {
        toast.error(r.data.message);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid coupon'); }
    finally { setCouponValidating(false); }
  };

  // Monthly/annual plans without a coupon renew automatically (Razorpay autopay mandate).
  const handleAutopayCheckout = async () => {
    setLoading(true);
    try {
      const subRes = await api.post(`${API_URL}/payments/create-subscription`, {
        email: email.toLowerCase(), full_name: fullName, plan: selectedPlan, billing_cycle: billingCycle,
      });
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        subscription_id: subRes.data.subscription_id,
        name: 'SALESQUALIFYD',
        description: `${plan.name} Plan — renews ${billingCycle === 'annual' ? 'yearly' : 'monthly'}`,
        handler: async (response) => {
          try {
            await api.post(`${API_URL}/payments/verify`, {
              payment_method: 'razorpay_subscription',
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              ref_code: localStorage.getItem('sqd_ref') || '',
            });
            toast.success('Payment successful! Check your email to complete setup.');
            navigate('/payment-success', { state: { email, message: 'Please check your email (including spam folder) for profile setup instructions.' } });
          } catch (e) { toast.error('Payment verification failed. Contact support.'); }
        },
        prefill: { email, name: fullName },
        theme: { color: '#8B1A1A' },
        modal: { ondismiss: () => { setLoading(false); toast.info('Payment cancelled'); } },
      };
      new window.Razorpay(options).open();
      setLoading(false);
    } catch (e) {
      setLoading(false);
      if (e.response?.status === 409) { toast.error(e.response.data.detail, { duration: 8000 }); return; }
      // Autopay unavailable (e.g. gateway setting) — never block the signup: use the one-time checkout.
      handleRazorpayPayment(true);
    }
  };

  const handleRazorpayPayment = async (oneTime = false) => {
    if (!email || !fullName) { toast.error('Please enter your email and full name'); return; }
    if (!couponApplied && !oneTime && autopayAvailable) return handleAutopayCheckout();
    setLoading(true);
    try {
      const orderRes = await api.post(`${API_URL}/payments/create-order`, {
        email: email.toLowerCase(), full_name: fullName, payment_method: 'razorpay',
        plan: selectedPlan, billing_cycle: billingCycle,
        coupon_code: couponApplied ? couponCode : null,
      });
      // 409 = existing active user is caught in the catch block below
      const { order_id, amount, currency } = orderRes.data;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: Math.round(amount * 100),
        currency,
        name: 'SALESQUALIFYD',
        description: `${plan.name} Plan — ${billingCycle === 'annual' ? 'Annual' : 'Monthly'}`,
        order_id,
        handler: async (response) => {
          try {
            const vr = await api.post(`${API_URL}/payments/verify`, {
              payment_method: 'razorpay',
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: selectedPlan, billing_cycle: billingCycle,
              ref_code: localStorage.getItem('sqd_ref') || '',
            });
            if (vr.data.requires_setup) {
              toast.success('Payment successful! Check your email to complete setup.');
              navigate('/payment-success', { state: { email, message: vr.data.redirect_message } });
            } else {
              toast.success(`Thank you! Your ${plan.name} subscription is now active.`);
              localStorage.setItem('token', vr.data.access_token);
              navigate('/dashboard');
            }
          } catch (e) { toast.error('Payment verification failed. Contact support.'); }
        },
        prefill: { email, name: fullName },
        theme: { color: '#8B1A1A' },
        modal: { ondismiss: () => { setLoading(false); toast.info('Payment cancelled'); } },
      };
      new window.Razorpay(options).open();
      setLoading(false);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Payment failed';
      if (e.response?.status === 409) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail);
      }
      setLoading(false);
    }
  };

  const handlePayPalPayment = async () => {
    if (!email || !fullName) { toast.error('Please enter your email and full name'); return; }
    setLoading(true);
    try {
      const orderRes = await api.post(`${API_URL}/payments/create-order`, {
        email: email.toLowerCase(), full_name: fullName, payment_method: 'paypal',
        plan: selectedPlan, billing_cycle: billingCycle,
        coupon_code: couponApplied ? couponCode : null,
      });
      // For PayPal, we show manual instructions for now (recurring billing managed via email reminders)
      toast.success('Order created. Complete payment via PayPal.');
      // If PayPal SDK is available, render buttons
      if (window.paypal) {
        const container = document.getElementById('paypal-button-container');
        if (container) container.innerHTML = '';
        window.paypal.Buttons({
          createOrder: (data, actions) => actions.order.create({
            purchase_units: [{ amount: { value: finalAmount.toFixed(2), currency_code: 'USD' }, description: `SALESQUALIFYD ${plan.name} — ${billingCycle}` }],
            application_context: { shipping_preference: 'NO_SHIPPING' },
          }),
          onApprove: async (data, actions) => {
            const order = await actions.order.capture();
            try {
              const vr = await api.post(`${API_URL}/payments/verify`, {
                payment_method: 'paypal', paypal_payment_id: order.id,
                email: email.toLowerCase(), name: fullName,
                plan: selectedPlan, billing_cycle: billingCycle,
              });
              if (vr.data.requires_setup) {
                toast.success('Payment successful! Check your email to complete setup.');
                navigate('/payment-success', { state: { email } });
              } else {
                toast.success(`Thank you! Your ${plan.name} subscription is now active.`);
                localStorage.setItem('token', vr.data.access_token);
                navigate('/dashboard');
              }
            } catch (e) { toast.error('Verification failed. Contact support.'); }
          },
          onError: () => { toast.error('PayPal payment failed.'); setLoading(false); },
          onCancel: () => { toast.info('Payment cancelled'); setLoading(false); },
        }).render('#paypal-button-container');
      }
      setLoading(false);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to create order';
      if (e.response?.status === 409) {
        toast.error(detail, { duration: 8000 });
      } else {
        toast.error(detail);
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <button onClick={() => navigate(-1)} className="absolute left-4 top-4 text-gray-400 hover:text-gray-600"><ArrowLeft className="w-5 h-5" /></button>
          <img src="/salesqualifyd-logo.png" alt="Logo" className="h-10 mx-auto mb-3" onError={e => e.target.style.display='none'} />
          <h1 className="text-2xl font-bold text-gray-900">Choose Your Plan</h1>
          <p className="text-gray-500 text-sm mt-1">Start growing your business today</p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-100 rounded-full p-1 flex" data-testid="billing-toggle">
            <button onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </button>
            <button onClick={() => setBillingCycle('annual')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'annual' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
              Annual
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {plans.filter(p => p.id !== 'enterprise').map(p => (
            <button key={p.id} onClick={() => setSelectedPlan(p.id)} data-testid={`plan-card-${p.id}`}
              className={`text-left p-5 rounded-xl border-2 transition-all ${selectedPlan === p.id ? 'border-red-800 bg-red-50/50 ring-1 ring-red-200' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-gray-900">{p.name}</h3>
                {p.id === 'growth' && <span className="text-[9px] font-bold bg-red-800 text-white px-2 py-0.5 rounded-full uppercase">Popular</span>}
              </div>
              <div className="mb-3">
                <span className="text-3xl font-bold text-gray-900">${billingCycle === 'annual' ? p.annual_price : p.monthly_price}</span>
                <span className="text-gray-500 text-sm">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
              </div>
              <ul className="space-y-1.5">
                {(PLAN_FEATURES[p.id] || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          ))}
        </div>

        {/* Checkout Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border max-w-lg mx-auto">
          <h2 className="font-bold text-lg text-gray-900 mb-1">{plan.name} Plan — {billingCycle === 'annual' ? 'Annual' : 'Monthly'}</h2>
          <p className="text-sm text-gray-500 mb-4">${basePrice} {billingCycle === 'annual' ? '/year' : '/month'} {couponApplied && <span className="text-green-600 font-medium">→ ${finalAmount} after coupon</span>}</p>

          <div className="space-y-3 mb-4">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" data-testid="checkout-email"
              className="w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-700 focus:border-transparent" />
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Full name" data-testid="checkout-name"
              className="w-full px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-red-700 focus:border-transparent" />
          </div>

          {/* Coupon */}
          <div className="mb-4">
            {!couponApplied ? (
              <div className="flex gap-2">
                <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} placeholder="Coupon code"
                  className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono" />
                <button onClick={handleApplyCoupon} disabled={couponValidating || !couponCode.trim()}
                  className="px-4 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 disabled:opacity-50">
                  {couponValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <span className="text-sm text-green-700 font-medium">{couponCode} — ${couponDiscount} off</span>
                <button onClick={() => { setCouponApplied(false); setCouponDiscount(0); setCouponCode(''); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button onClick={() => setPaymentMethod('razorpay')} data-testid="pay-razorpay"
              className={`p-3 border-2 rounded-lg text-center transition-all ${paymentMethod === 'razorpay' ? 'border-red-800 bg-red-50' : 'border-gray-200'}`}>
              <CreditCard className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'razorpay' ? 'text-red-800' : 'text-gray-400'}`} />
              <div className="text-xs font-medium">Card / UPI</div>
              <div className="text-[10px] text-gray-400">Razorpay</div>
            </button>
            <button onClick={() => setPaymentMethod('paypal')} data-testid="pay-paypal"
              className={`p-3 border-2 rounded-lg text-center transition-all ${paymentMethod === 'paypal' ? 'border-red-800 bg-red-50' : 'border-gray-200'}`}>
              <DollarSign className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'paypal' ? 'text-red-800' : 'text-gray-400'}`} />
              <div className="text-xs font-medium">PayPal / Card</div>
              <div className="text-[10px] text-gray-400">International</div>
            </button>
          </div>

          {/* Pay Button */}
          {paymentMethod === 'razorpay' ? (
            <button onClick={() => handleRazorpayPayment()} disabled={loading || !email || !fullName} data-testid="pay-button"
              className="w-full bg-red-800 hover:bg-red-900 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50">
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : <><Lock className="w-4 h-4 inline mr-2" />Pay ${finalAmount} — Secure Checkout</>}
            </button>
          ) : null}
          {paymentMethod === 'razorpay' && (couponApplied || autopayAvailable) && (
            <p className="text-[11px] text-gray-500 text-center mt-2" data-testid="autopay-note">
              {couponApplied
                ? 'One-time payment with your coupon. You can turn on autopay later from Settings → Billing.'
                : `Autopay: renews automatically at $${basePrice}/${billingCycle === 'annual' ? 'year' : 'month'}. Cancel anytime from Settings → Billing.`}
            </p>
          )}
          {paymentMethod === 'razorpay' ? null : (
            <div>
              <button onClick={handlePayPalPayment} disabled={loading || !email || !fullName} data-testid="pay-button-paypal"
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-3 rounded-lg transition-all disabled:opacity-50 mb-2">
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Pay with PayPal'}
              </button>
              <div id="paypal-button-container"></div>
            </div>
          )}

          <div className="flex items-center justify-center gap-4 mt-4 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" />Secure</span>
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" />256-bit SSL</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />72hr money-back</span>
          </div>
        </div>

        <div className="text-center mt-4 text-xs text-gray-400">
          Already have an account? <button onClick={() => navigate('/login')} className="text-red-800 hover:underline">Login</button>
          {' | '}<a href="/privacy-policy" className="hover:underline">Privacy</a>
          {' | '}<a href="/terms-of-use" className="hover:underline">Terms</a>
          {' | '}<a href="/refund-policy" className="hover:underline">Refund</a>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
